import React, { useState, useMemo } from 'react';
import {
  Star,
  Lock,
  Unlock,
  Paperclip,
  MoreHorizontal,
  Archive,
  Trash2,
  Tag,
  Mail,
  MailOpen,
  ChevronDown,
  ChevronRight,
  Loader2,
  GripVertical,
  Eye,
  EyeOff,
  Filter
} from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { cn } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';

function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const hours = diff / (1000 * 60 * 60);
  const days = hours / 24;

  if (hours < 1) {
    const mins = Math.floor(diff / (1000 * 60));
    return `${mins}m ago`;
  } else if (hours < 24) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } else if (days < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

// Get date group label for email
function getDateGroup(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const twoDaysAgo = new Date(today);
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
  const startOfWeek = new Date(today);
  startOfWeek.setDate(startOfWeek.getDate() - 7);
  const startOfMonth = new Date(today);
  startOfMonth.setDate(startOfMonth.getDate() - 30);

  const emailDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (emailDate >= today) {
    return { key: 'today', label: 'Today', order: 0 };
  } else if (emailDate >= yesterday) {
    return { key: 'yesterday', label: 'Yesterday', order: 1 };
  } else if (emailDate >= twoDaysAgo) {
    return { key: 'twoDaysAgo', label: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }), order: 2 };
  } else if (emailDate >= startOfWeek) {
    return { key: 'thisWeek', label: 'This Week', order: 3 };
  } else if (emailDate >= startOfMonth) {
    return { key: 'thisMonth', label: 'This Month', order: 4 };
  } else {
    return { key: 'older', label: 'Older', order: 5 };
  }
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function EmailListItem({ email, isSelected, isActive, onSelect, onClick, onToggleStar, onDelete, onArchive, onMarkUnread, labels, onDragStart, onDragEnd, isDarkMode, isMobile }) {
  const emailLabels = (email.labels || []).map(id => labels?.find(l => l.id === id)).filter(Boolean);

  // Theme-aware classes
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const hoverBg = isDarkMode ? 'hover:bg-[#252540]' : 'hover:bg-gray-50';
  const activeBg = isDarkMode ? 'bg-[#252540]' : 'bg-blue-50';
  const unreadBg = isDarkMode ? 'bg-[#1f1f35]' : 'bg-blue-50/50';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const buttonHover = isDarkMode ? 'hover:bg-[#2d2d44]' : 'hover:bg-gray-100';

  // Mobile layout - matches reference image style
  if (isMobile) {
    return (
      <div
        className={cn(
          `flex items-start gap-2 px-3 py-2.5 border-b ${borderColor} cursor-pointer transition-all`,
          hoverBg,
          isActive && `${activeBg} border-l-2 border-l-[#6D4AFF]`,
          !email.read && unreadBg
        )}
        onClick={onClick}
        data-testid={`email-item-mobile-${email.id}`}
      >
        {/* Main Content */}
        <div className="flex-1 min-w-0 pr-1">
          {/* Row 1: From Name + Date */}
          <div className="flex items-center gap-1 mb-0.5">
            <span className={cn(
              "text-sm truncate flex-1 min-w-0",
              email.read ? textSecondary : "text-[#00bcd4] font-semibold"
            )}>
              {email.from_name}
            </span>
            <span className={`text-[11px] ${textMuted} flex-shrink-0 whitespace-nowrap`}>
              {formatDate(email.created_at)}
            </span>
          </div>

          {/* Row 2: Subject + Labels */}
          <div className="flex items-center gap-1 mb-0.5">
            <span className={cn(
              "text-[13px] truncate flex-1 min-w-0",
              email.read ? textMuted : `${textPrimary} font-medium`
            )}>
              {email.subject}
            </span>
            {/* Labels as dots */}
            {emailLabels.length > 0 && (
              <div className="flex gap-0.5 flex-shrink-0">
                {emailLabels.map((label) => (
                  <span
                    key={label.id}
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: label.color }}
                    title={label.name}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Row 3: Preview */}
          <p className={`text-xs ${textMuted} truncate`}>
            {email.preview}
          </p>
        </div>

        {/* Right column: Star + Lock icons */}
        <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
          <button
            className="p-0.5"
            onClick={(e) => {
              e.stopPropagation();
              onToggleStar(email.id);
            }}
          >
            <Star
              className={cn(
                "w-4 h-4 transition-colors",
                email.starred ? "fill-yellow-500 text-yellow-500" : `${textMuted}`
              )}
            />
          </button>
          {email.encrypted && (
            <Lock className="w-3.5 h-3.5 text-green-500" />
          )}
          {email.attachments?.length > 0 && (
            <Paperclip className={`w-3.5 h-3.5 ${textMuted}`} />
          )}
        </div>
      </div>
    );
  }

  // Desktop layout - original design
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, email)}
      onDragEnd={onDragEnd}
      className={cn(
        `group flex items-start gap-2 px-4 py-3 border-b ${borderColor} cursor-pointer transition-all overflow-hidden w-full`,
        hoverBg,
        isActive && `${activeBg} border-l-2 border-l-[#6D4AFF]`,
        !email.read && unreadBg
      )}
      onClick={onClick}
    >
      {/* Drag Handle */}
      <div className="pt-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <GripVertical className="w-4 h-4 text-gray-500" />
      </div>

      {/* Checkbox */}
      <div className="pt-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={onSelect}
          className="border-gray-600 data-[state=checked]:bg-[#6D4AFF] data-[state=checked]:border-[#6D4AFF]"
        />
      </div>

      {/* Star */}
      <button
        className="pt-1 flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onToggleStar(email.id);
        }}
      >
        <Star
          className={cn(
            "w-4 h-4 transition-colors",
            email.starred ? "fill-yellow-500 text-yellow-500" : "text-gray-500 hover:text-yellow-500"
          )}
        />
      </button>

      {/* Unread indicator */}
      {!email.read && (
        <div className="pt-2 flex-shrink-0">
          <div className="w-2 h-2 rounded-full bg-[#6D4AFF]" />
        </div>
      )}

      {/* Avatar */}
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
        <span className="text-white text-xs font-medium">{getInitials(email.from_name)}</span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className={cn(
            "text-sm truncate",
            email.read ? textSecondary : `${textPrimary} font-semibold`
          )}>
            {email.from_name}
          </span>
          <span className={`text-xs ${textMuted} flex-shrink-0`}>{formatDate(email.created_at)}</span>
        </div>

        <div className="flex items-center gap-2 mb-1">
          <span className={cn(
            "text-sm truncate flex-1",
            email.read ? textMuted : `${textPrimary} font-medium`
          )}>
            {email.subject}
          </span>
          
          {/* Labels */}
          {emailLabels.length > 0 && (
            <div className="flex gap-1 flex-shrink-0">
              {emailLabels.map((label) => (
                <span
                  key={label.id}
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: label.color }}
                  title={label.name}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <p className={`text-xs ${textMuted} truncate flex-1`}>
            {email.preview}
          </p>
          
          {/* Icons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {email.attachments?.length > 0 && (
              <Paperclip className={`w-3.5 h-3.5 ${textMuted}`} />
            )}
            {email.encrypted && (
              <Lock className="w-3.5 h-3.5 text-green-500" />
            )}
          </div>
        </div>
      </div>

      {/* Hover Actions */}
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 ${textMuted} ${isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'} ${buttonHover}`}
            onClick={(e) => {
              e.stopPropagation();
              onMarkUnread(email.id);
            }}
            title={email.read ? "Mark as unread" : "Mark as read"}
          >
            {email.read ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 ${textMuted} ${isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'} ${buttonHover}`}
            onClick={(e) => {
              e.stopPropagation();
              onArchive(email.id);
            }}
          >
            <Archive className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 ${textMuted} hover:text-red-400 ${buttonHover}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(email.id);
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function EmailList({
  emails,
  selectedEmails,
  activeEmail,
  onSelectEmail,
  onSelectAll,
  onOpenEmail,
  onToggleStar,
  onDelete,
  onArchive,
  onMarkUnread,
  onMoveToFolder,
  folderName,
  loading,
  labels
}) {
  const { isDarkMode } = useTheme();
  const [filterUnread, setFilterUnread] = useState(false);
  const [draggedEmail, setDraggedEmail] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Handle resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const allSelected = emails.length > 0 && selectedEmails.length === emails.length;
  const someSelected = selectedEmails.length > 0 && selectedEmails.length < emails.length;

  // Filter emails based on unread filter
  const filteredEmails = filterUnread ? emails.filter(e => !e.read) : emails;

  // Group emails by date
  const groupedEmails = useMemo(() => {
    const groups = {};
    filteredEmails.forEach(email => {
      const group = getDateGroup(email.created_at);
      if (!groups[group.key]) {
        groups[group.key] = { ...group, emails: [] };
      }
      groups[group.key].emails.push(email);
    });
    // Sort groups by order and return as array
    return Object.values(groups).sort((a, b) => a.order - b.order);
  }, [filteredEmails]);

  // Track collapsed state for each group
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Theme-aware classes
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-white';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textColor = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const textHover = isDarkMode ? 'hover:text-white' : 'hover:text-gray-900';
  const buttonHover = isDarkMode ? 'hover:bg-[#2d2d44]' : 'hover:bg-gray-100';
  const dropdownBg = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const dropdownText = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const dropdownHover = isDarkMode ? 'hover:text-white focus:bg-[#2d2d44]' : 'hover:text-gray-900 focus:bg-gray-100';
  const separatorBg = isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200';
  const sectionHeaderBg = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-gray-50';

  // Drag handlers
  const handleDragStart = (e, email) => {
    setDraggedEmail(email);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ 
      emailId: email.id, 
      emailIds: selectedEmails.includes(email.id) ? selectedEmails : [email.id] 
    }));
    
    // Create a custom drag image
    const dragImage = document.createElement('div');
    dragImage.className = 'bg-[#6D4AFF] text-white px-3 py-2 rounded-lg text-sm font-medium shadow-lg';
    const count = selectedEmails.includes(email.id) ? selectedEmails.length : 1;
    dragImage.textContent = count > 1 ? `${count} emails` : email.subject.slice(0, 30) + '...';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-1000px';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = () => {
    setDraggedEmail(null);
  };

  return (
    <div className={`flex flex-col h-full w-full overflow-hidden ${bgPrimary}`}>
      {/* Toolbar */}
      <div className={`flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3 border-b ${borderColor} flex-shrink-0`}>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Checkbox - only on desktop */}
          {!isMobile && (
            <>
              <Checkbox
                checked={allSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                onCheckedChange={(checked) => onSelectAll(checked)}
                className="border-gray-600 data-[state=checked]:bg-[#6D4AFF] data-[state=checked]:border-[#6D4AFF]"
              />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`${textColor} ${textHover} ${buttonHover}`}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={`${dropdownBg} ${borderColor} z-50`}>
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => onSelectAll(true)}>
                    Select all
                  </DropdownMenuItem>
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => onSelectAll(false)}>
                    Select none
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={separatorBg} />
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => {
                    const unreadIds = emails.filter(e => !e.read).map(e => e.id);
                    onSelectAll(false);
                    unreadIds.forEach(id => onSelectEmail(id));
                  }}>
                    Select unread
                  </DropdownMenuItem>
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => {
                    const starredIds = emails.filter(e => e.starred).map(e => e.id);
                    onSelectAll(false);
                    starredIds.forEach(id => onSelectEmail(id));
                  }}>
                    Select starred
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}

          {/* Folder name on mobile */}
          {isMobile && (
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {folderName}
            </span>
          )}

          {/* Unread Filter Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilterUnread(!filterUnread)}
            className={cn(
              `${textColor} ${textHover} ${buttonHover} gap-1 sm:gap-2`,
              filterUnread && "bg-[#6D4AFF]/20 text-[#6D4AFF]"
            )}
          >
            <Filter className="w-4 h-4" />
            <span className="text-xs hidden sm:inline">Unread</span>
          </Button>

          {selectedEmails.length > 0 && !isMobile && (
            <div className="flex items-center gap-1 ml-2">
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${textColor} ${textHover} ${buttonHover}`}
                onClick={() => selectedEmails.forEach(id => onMarkUnread(id))}
                title="Mark as unread"
              >
                <EyeOff className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${textColor} ${textHover} ${buttonHover}`}
                onClick={() => selectedEmails.forEach(id => onArchive(id))}
              >
                <Archive className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={`h-8 w-8 ${textColor} hover:text-red-400 ${buttonHover}`}
                onClick={() => selectedEmails.forEach(id => onDelete(id))}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              
              {/* Move to folder dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`h-8 ${textColor} ${textHover} ${buttonHover} gap-1`}>
                    <Mail className="w-4 h-4" />
                    <span className="text-xs">Move</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={`${dropdownBg} ${borderColor} z-50`}>
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => onMoveToFolder(selectedEmails, 'inbox')}>
                    Move to Inbox
                  </DropdownMenuItem>
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => onMoveToFolder(selectedEmails, 'archive')}>
                    Move to Archive
                  </DropdownMenuItem>
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => onMoveToFolder(selectedEmails, 'spam')}>
                    Move to Spam
                  </DropdownMenuItem>
                  <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`} onClick={() => onMoveToFolder(selectedEmails, 'trash')}>
                    Move to Trash
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-2 text-xs sm:text-sm ${textColor}`}>
          <span>{filteredEmails.length} {isMobile ? '' : 'messages'}</span>
          {filterUnread && emails.length !== filteredEmails.length && (
            <span className="text-[#6D4AFF]">(filtered)</span>
          )}
          {selectedEmails.length > 0 && !isMobile && (
            <span className="text-[#6D4AFF]">({selectedEmails.length} selected)</span>
          )}
        </div>
      </div>

      {/* Email List */}
      <ScrollArea className="flex-1 w-full overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#6D4AFF]" />
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className={`flex flex-col items-center justify-center h-full py-20 ${textColor}`}>
            <Mail className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`text-lg font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No messages</p>
            <p className="text-sm">
              {filterUnread ? 'No unread messages' : `Your ${folderName.toLowerCase()} is empty`}
            </p>
            {filterUnread && emails.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilterUnread(false)}
                className="mt-4 text-[#6D4AFF] hover:text-[#8B5CF6]"
              >
                Show all messages
              </Button>
            )}
          </div>
        ) : (
          <div className="w-full overflow-hidden">
            {groupedEmails.map((group) => (
              <Collapsible
                key={group.key}
                open={!collapsedGroups[group.key]}
                onOpenChange={() => toggleGroup(group.key)}
              >
                {/* Date Section Header */}
                <CollapsibleTrigger className={`w-full sticky top-0 z-10 border-b ${borderColor}`}>
                  <div className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer ${isDarkMode ? 'bg-[#252540] hover:bg-[#2d2d50]' : 'bg-gray-100 hover:bg-gray-150'}`}>
                    {collapsedGroups[group.key] ? (
                      <ChevronRight className={`w-4 h-4 ${isDarkMode ? 'text-[#6D4AFF]' : 'text-[#6D4AFF]'}`} />
                    ) : (
                      <ChevronDown className={`w-4 h-4 ${isDarkMode ? 'text-[#6D4AFF]' : 'text-[#6D4AFF]'}`} />
                    )}
                    <span className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {group.label}
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} ml-auto`}>
                      {group.emails.length}
                    </span>
                  </div>
                </CollapsibleTrigger>
                
                {/* Emails in this group */}
                <CollapsibleContent>
                  {group.emails.map((email) => (
                    <EmailListItem
                      key={email.id}
                      email={email}
                      isSelected={selectedEmails.includes(email.id)}
                      isActive={activeEmail?.id === email.id}
                      onSelect={() => onSelectEmail(email.id)}
                      onClick={() => onOpenEmail(email)}
                      onToggleStar={onToggleStar}
                      onDelete={onDelete}
                      onArchive={onArchive}
                      onMarkUnread={onMarkUnread}
                      labels={labels}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      isDarkMode={isDarkMode}
                      isMobile={isMobile}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
